// Data layer for the Genie Templates catalogue.
//
// Single source of truth: playbookGoals from
// src/pages/UserHub/Playbooks/data/playbook-templates.ts. Every play there
// becomes a GenieTemplate card here. Click -> /user-hub/genie-setup?template=
// <play.id> -> useTemplatePreload.ts dispatches the full templateConfig into
// setup state -> user lands in the existing genie-creation flow with prompt,
// first_message, tools, and KB pre-filled.
//
// Per-template overrides:
//   PLAY_MASCOT_MAP — which role mascot each play uses
//   PLAY_CATEGORY_MAP — which catalogue category each goal sits in
//   PLAY_JOBS_OVERRIDE — JTBD bullets shown on the kickstart screen

import { roleMascots, type RoleSlug } from "@/data/genieMascots";
import { playbookGoals } from "@/pages/UserHub/Playbooks/data/playbook-templates";

export type CatalogueCategory = {
  id: string;
  label: string;
  description: string;
  iconUrl: string;
};

// 7 business functions — match playbookGoals[].id one-to-one.
export const categories: CatalogueCategory[] = [
  {
    id: "customer-service",
    label: "Customer Service",
    description: "Genies that take care of your existing customers.",
    iconUrl: "/icons/capabilities/icon-product-support.png",
  },
  {
    id: "account-management",
    label: "Account Management",
    description: "Genies that grow and retain your customer base.",
    iconUrl: "/icons/capabilities/icon-how-it-works.png",
  },
  {
    id: "marketing",
    label: "Marketing",
    description: "Genies that get you in front of the right people.",
    iconUrl: "/icons/capabilities/icon-voice-experiences.png",
  },
  {
    id: "operations",
    label: "Operations",
    description: "Genies that run the back-office work that eats your week.",
    iconUrl: "/icons/capabilities/icon-internal-info.png",
  },
  {
    id: "sales",
    label: "Sales",
    description: "Genies that win new business — qualify, quote, dial, close.",
    iconUrl: "/icons/capabilities/icon-sales-lead-capture.png",
  },
  {
    id: "people",
    label: "People (HR & L&D)",
    description: "Genies for hiring, employee experience, and learning.",
    iconUrl: "/icons/capabilities/icon-playbooks.png",
  },
  {
    id: "compliance-insights",
    label: "Compliance & Insights",
    description: "Genies that make sure nothing slips and capture the signal.",
    iconUrl: "/icons/capabilities/icon-playbooks.png",
  },
];

export type GenieTemplate = {
  slug: string; // = play.id, what useTemplatePreload looks up
  name: string;
  tagline: string;
  description: string;
  categoryId: CatalogueCategory["id"];
  mascot: string;
  jobsToBeDone?: string[];
  setupMinutes: number;
};

// Map each goal.id -> catalogue category id (1:1, since we redesigned).
const PLAY_CATEGORY_MAP: Record<string, string> = {
  sales: "sales",
  marketing: "marketing",
  "customer-service": "customer-service",
  "account-management": "account-management",
  operations: "operations",
  people: "people",
  "compliance-insights": "compliance-insights",
};

// Map each play.id -> closest role mascot. Plays without an entry fall back
// to the Support mascot. Many new genie types reuse existing mascots until
// dedicated illustrations are generated.
const PLAY_MASCOT_MAP: Record<string, RoleSlug> = {
  // Sales
  sales: "sales",
  "lead-capture": "lead-capture",
  outreach: "sales",
  outbound: "sales",
  quote: "quote",
  estimate: "quote",
  upsell: "sales",
  // Marketing
  campaign: "marketing",
  "lead-magnet": "marketing",
  event: "marketing",
  brand: "marketing",
  // Customer Service
  support: "support",
  "after-hours": "after-hours",
  escalation: "support",
  returns: "returns",
  complaints: "feedback",
  status: "operations",
  // Account Management
  "customer-onboarding": "onboarding",
  "check-in": "feedback",
  renewal: "sales",
  review: "feedback",
  // Operations
  reception: "receptionist",
  concierge: "concierge",
  booking: "booking",
  reminder: "booking",
  form: "form",
  order: "form",
  // People (HR + L&D)
  recruiter: "onboarding",
  "employee-onboarding": "onboarding",
  "hr-help": "support",
  "it-help": "support",
  training: "knowledge",
  // Compliance & Insights
  compliance: "compliance",
  "incident-reporting": "compliance",
  survey: "survey",
  testimonial: "feedback",
  feedback: "feedback",
};

// Hand-written jobsToBeDone bullets (shown on the kickstart "what this Genie
// will do for you" panel). Three crisp jobs per Genie.
const PLAY_JOBS_OVERRIDE: Record<string, string[]> = {
  // Sales
  sales: [
    "Qualify with budget, timing, and fit questions",
    "Book a meeting directly on your calendar",
    "Send a recap to the rep before the call",
  ],
  "lead-capture": [
    "Greet visitors on your key pages",
    "Capture name + need + context",
    "Drop them into your CRM with notes",
  ],
  outreach: [
    "Open with the personalized hook",
    "Handle objections without being pushy",
    "Book the meeting or capture for nurture",
  ],
  outbound: [
    "Dial your prospect list automatically",
    "Run the opening and qualify in real time",
    "Book meetings or hang up politely",
  ],
  quote: [
    "Ask the scoping questions for your service",
    "Build the line items in real time",
    "Send a branded quote on the spot",
  ],
  estimate: [
    "Scope variable-scope work in a conversation",
    "Give a defensible range with caveats",
    "Book the site visit for a firm quote",
  ],
  upsell: [
    "Listen for cross-sell signals on routine calls",
    "Recommend the right next product",
    "Route hot interest to the account manager",
  ],
  // Marketing
  campaign: [
    "Carry your campaign message consistently",
    "Capture interest from people not ready yet",
    "Route hot leads straight to checkout",
  ],
  "lead-magnet": [
    "Replace your gated PDF form with a chat",
    "Learn 10x more about each lead",
    "Deliver the content and tag in CRM",
  ],
  event: [
    "Answer schedule, speaker, and logistics questions",
    "Capture registration and confirmations",
    "Handle the FAQ flood without burning your team",
  ],
  brand: [
    "Pitch what you do in 30 seconds",
    "Answer pricing and packaging clearly",
    "Route hot traffic to sales, browsers to learn-more",
  ],
  // Customer Service
  support: [
    "Answer FAQs instantly from your knowledge base",
    "Triage and tag tickets before they hit your team",
    "Escalate to a human with full context",
  ],
  "after-hours": [
    "Greet every after-hours caller warmly",
    "Triage urgency and page on-call when needed",
    "Send a clean transcript to the on-call inbox",
  ],
  escalation: [
    "Try to resolve before handing off",
    "Capture the full context (issue, attempts, mood)",
    "Hand off so the human doesn't ask twice",
  ],
  returns: [
    "Walk the customer through your returns flow",
    "Capture the actual reason in their words",
    "Flag suspicious return patterns",
  ],
  complaints: [
    "Let customers vent without escalating",
    "Capture the underlying issue, not just the surface",
    "Route by severity to the right team",
  ],
  status: [
    "Answer 'is it down?' in one voice across all channels",
    "Share real ETAs from engineering",
    "Email customers automatically when resolved",
  ],
  // Account Management
  "customer-onboarding": [
    "Greet new customers and learn their goal",
    "Walk them through the setup steps that match",
    "Hand off to CS at the right moments",
  ],
  "check-in": [
    "Reach out on a 30 / 60 / 90 day cadence",
    "Surface issues and feedback early",
    "Flag at-risk accounts with the actual reason",
  ],
  renewal: [
    "Open renewal conversations 30-60 days out",
    "Surface real value delivered (not generic)",
    "Route at-risk renewals to the AM immediately",
  ],
  review: [
    "Reach out at moments of customer success",
    "Send the review link to the right platform",
    "Capture hesitation as a retention signal",
  ],
  // Operations
  reception: [
    "Greet and qualify every caller",
    "Route to the right person or extension",
    "Take a clean message when nobody's free",
  ],
  concierge: [
    "Recognise VIP customers by phone or context",
    "Remember their preferences across calls",
    "Pre-empt their next ask",
  ],
  booking: [
    "Book straight into your calendar",
    "Send confirmations and reminders automatically",
    "Handle reschedule requests",
  ],
  reminder: [
    "Confirm appointments 24-48 hours ahead",
    "Handle reschedules in the same call",
    "Flag no-shows so slots can be reclaimed",
  ],
  form: [
    "Collect the same fields as your form",
    "Validate every answer as it comes in",
    "Drop a clean row into your system",
  ],
  order: [
    "Take orders item by item, repeated back",
    "Handle modifications and pricing",
    "Confirm, route to fulfillment, send receipt",
  ],
  // People (HR + L&D)
  recruiter: [
    "Screen applicants with role-specific questions",
    "Score candidates against your criteria",
    "Book strong fits into the hiring manager's calendar",
  ],
  "employee-onboarding": [
    "Welcome new hires before day one",
    "Answer the practical questions (parking, dress, IT)",
    "Capture blockers and route to HR/IT",
  ],
  "hr-help": [
    "Answer PTO, benefits, and policy questions",
    "Always escalate sensitive matters to humans",
    "Keep employee data confidential",
  ],
  "it-help": [
    "Run common troubleshooting (WiFi, VPN, software)",
    "Open tickets with full context for hard issues",
    "Escalate outages and security events fast",
  ],
  training: [
    "Calibrate to what each employee already knows",
    "Run knowledge checks throughout",
    "Flag where someone needs extra support",
  ],
  // Compliance & Insights
  compliance: [
    "Run a regulated checklist as a conversation",
    "Capture answers verbatim",
    "Flag missing or risky responses",
  ],
  "incident-reporting": [
    "Take incident reports calmly and structured",
    "Tag severity and route to the right team",
    "Confirm next steps and follow-up time",
  ],
  survey: [
    "Run surveys as a chat, not a Likert form",
    "Probe interesting answers with follow-ups",
    "Summarise the insight, not the raw data",
  ],
  testimonial: [
    "Walk customers through challenge → solution → result",
    "Get explicit approval to use the quote",
    "Deliver polished assets to marketing",
  ],
  feedback: [
    "Let customers share without escalating tension",
    "Capture the underlying issue, not just the surface",
    "Route by severity with full context",
  ],
};

// Build the catalogue from playbookGoals — the live source of truth.
export const genieTemplates: GenieTemplate[] = playbookGoals.flatMap((goal) =>
  goal.plays.map((play): GenieTemplate => ({
    slug: play.id,
    name: play.name,
    tagline: play.tagline,
    description: play.strategy,
    categoryId: PLAY_CATEGORY_MAP[goal.id] ?? "operations",
    mascot: roleMascots[PLAY_MASCOT_MAP[play.id] ?? "support"],
    jobsToBeDone: PLAY_JOBS_OVERRIDE[play.id],
    setupMinutes: 3,
  })),
);

export const templatesByCategory = (categoryId: string) =>
  genieTemplates.filter((t) => t.categoryId === categoryId);

export const findTemplate = (slug: string) =>
  genieTemplates.find((t) => t.slug === slug);
